import { Group, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	getReferralRedemptionFilters,
	listReferralRedemptions,
	referralKeys,
} from "@/features/referral/api/admin-referral";
import type { ListReferralRedemptionsParams } from "@/features/referral/api/types";
import type { PageLimit } from "@/shared/api/types";
import { JournalShell } from "@/shared/ui/journal-shell";

function staffLabel(fullName: string, email: string): string {
	return `${fullName} · ${email}`;
}

export function ReferralRedemptionsPage() {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [staffId, setStaffId] = useState<string | null>(null);
	const [rewardName, setRewardName] = useState<string | null>(null);
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState<PageLimit>(20);
	const [sort, setSort] =
		useState<NonNullable<ListReferralRedemptionsParams["sort"]>>("createdAt");

	const params: ListReferralRedemptionsParams = useMemo(
		() => ({
			search:
				debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : undefined,
			staffId: staffId ?? undefined,
			rewardName: rewardName ?? undefined,
			from: from || undefined,
			to: to || undefined,
			page,
			limit,
			sort,
			order: "DESC",
		}),
		[debouncedSearch, staffId, rewardName, from, to, page, limit, sort],
	);

	const filtersQuery = useQuery({
		queryKey: referralKeys.redemptionFilters,
		queryFn: getReferralRedemptionFilters,
	});

	const listQuery = useQuery({
		queryKey: referralKeys.redemptions(params),
		queryFn: () => listReferralRedemptions(params),
	});

	return (
		<JournalShell
			title={t("referral.journalTitle")}
			subtitle={t("referral.journalSubtitle")}
			isLoading={listQuery.isLoading}
			error={listQuery.error}
			errorTitle={t("referral.journalLoadError")}
			onRetry={() => void listQuery.refetch()}
			empty={t("referral.journalEmpty")}
			hasRows={Boolean(listQuery.data && listQuery.data.data.length > 0)}
			page={page}
			total={listQuery.data?.totalItemsCount ?? 0}
			hasNextPage={Boolean(listQuery.data?.hasNextPage)}
			isFetching={listQuery.isFetching}
			onPageChange={setPage}
			filters={
				<Stack gap="md">
					<Group grow preventGrowOverflow={false} wrap="wrap">
						<TextInput
							label={t("referral.guestSearch")}
							placeholder={t("referral.guestSearchHint")}
							value={search}
							onChange={(event) => {
								setSearch(event.currentTarget.value);
								setPage(1);
							}}
						/>
						<Select
							label={t("referral.staff")}
							clearable
							searchable
							data={(filtersQuery.data?.staff ?? []).map((row) => ({
								value: row.id,
								label: staffLabel(row.fullName, row.email),
							}))}
							value={staffId}
							onChange={(value) => {
								setStaffId(value);
								setPage(1);
							}}
						/>
						<Select
							label={t("referral.item")}
							clearable
							searchable
							data={filtersQuery.data?.positions ?? []}
							value={rewardName}
							onChange={(value) => {
								setRewardName(value);
								setPage(1);
							}}
						/>
					</Group>
					<Group grow preventGrowOverflow={false} wrap="wrap" maw={720}>
						<TextInput
							label={t("referral.from")}
							type="date"
							value={from}
							onChange={(event) => {
								setFrom(event.currentTarget.value);
								setPage(1);
							}}
						/>
						<TextInput
							label={t("referral.to")}
							type="date"
							value={to}
							onChange={(event) => {
								setTo(event.currentTarget.value);
								setPage(1);
							}}
						/>
						<Select
							label={t("common.sort")}
							data={[
								{ value: "createdAt", label: t("referral.sortDate") },
								{ value: "pointsCost", label: t("referral.sortPoints") },
							]}
							value={sort}
							onChange={(value) => {
								if (value === "createdAt" || value === "pointsCost") {
									setSort(value);
									setPage(1);
								}
							}}
						/>
						<Select
							label={t("common.rows")}
							data={[
								{ value: "20", label: "20" },
								{ value: "50", label: "50" },
								{ value: "100", label: "100" },
							]}
							value={String(limit)}
							onChange={(value) => {
								if (value) {
									setLimit(Number(value) as PageLimit);
									setPage(1);
								}
							}}
						/>
					</Group>
				</Stack>
			}
		>
			<Table.ScrollContainer type="native" minWidth={880}>
				<Table verticalSpacing="sm" highlightOnHover>
					<Table.Thead>
						<Table.Tr>
							<Table.Th>{t("referral.when")}</Table.Th>
							<Table.Th>{t("referral.guest")}</Table.Th>
							<Table.Th>{t("referral.item")}</Table.Th>
							<Table.Th>{t("referral.pointsSpent")}</Table.Th>
							<Table.Th>{t("referral.staff")}</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{(listQuery.data?.data ?? []).map((row) => (
							<Table.Tr key={row.id}>
								<Table.Td>
									{new Date(row.createdAt).toLocaleString("uk-UA")}
								</Table.Td>
								<Table.Td>
									{row.guest ? (
										<Stack gap={0}>
											<Text size="sm">
												{[row.guest.name, row.guest.surname]
													.filter(Boolean)
													.join(" ") || t("common.dash")}
											</Text>
											<Text size="xs" c="dimmed">
												{row.guest.phone}
											</Text>
										</Stack>
									) : (
										<Text c="dimmed">{t("common.dash")}</Text>
									)}
								</Table.Td>
								<Table.Td>{row.rewardName}</Table.Td>
								<Table.Td>{row.pointsCost}</Table.Td>
								<Table.Td>
									{row.staff ? (
										<Stack gap={0}>
											<Text size="sm">{row.staff.fullName}</Text>
											<Text size="xs" c="dimmed">
												{row.staff.email}
											</Text>
										</Stack>
									) : (
										<Text c="dimmed">{t("common.dash")}</Text>
									)}
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			</Table.ScrollContainer>
		</JournalShell>
	);
}
